import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";

import {
  getDirection,
  getLanguage,
  type Language,
  setLanguage as applyLanguage,
  subscribeLanguage,
  translate,
} from "@/i18n";

export type { Language };
export type TranslateFn = (key: string, ...args: (string | number)[]) => string;

interface LanguageContextProps {
  language: Language;
  dir: "ltr" | "rtl";
  t: TranslateFn;
  setLanguage: (lang: Language) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextProps>({
  language: "ar",
  dir: "rtl",
  t: translate,
  setLanguage: async () => {},
});

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const language = useSyncExternalStore(
    subscribeLanguage,
    getLanguage,
    getLanguage,
  );
  const dir = getDirection(language);

  // Re-created per language so consumers re-render with the new dictionary.
  const t: TranslateFn = useCallback(
    (key, ...args) => translate(key, ...args),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );

  const setLanguage = useCallback(async (lang: Language) => {
    applyLanguage(lang);
  }, []);

  const value = useMemo(
    () => ({ language, dir, t, setLanguage }),
    [language, dir, t, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
